import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../../components/SEOHead';

export default function RoomInspirationPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Room Inspiration - Interior Decor Solutions',
    url: `${siteUrl}/room-inspiration`,
    description: 'Explore curated decor ideas and products for every space in your home. Browse by room to find the perfect pieces for your living room, bedroom, kitchen, bathroom, and more.',
    publisher: {
      '@type': 'Organization',
      name: 'Interior Decor Solutions',
    },
  };

  const roomCategories = [
    {
      id: 'living-room',
      name: 'Living Room',
      icon: 'ri-sofa-line',
      image: 'https://public.readdy.ai/ai/img_res/78ef344eeed1d2344495a1ba5cc3c7e8.jpg',
      subcategories: ['Sofas & Sectionals', 'Coffee Tables', 'Wall Art', 'Lighting'],
      description: 'Create a welcoming space where comfort meets style. From plush sofas to statement lighting, find everything you need to make your living room the heart of your home.',
      link: '/living-room'
    },
    {
      id: 'bedroom',
      name: 'Bedroom',
      icon: 'ri-hotel-bed-line',
      image: 'https://readdy.ai/api/search-image?query=Serene%20minimalist%20bedroom%20interior%20with%20comfortable%20bed%2C%20soft%20bedding%2C%20neutral%20tones%2C%20clean%20lines%2C%20peaceful%20atmosphere%2C%20simple%20elegant%20decor%2C%20warm%20lighting%2C%20contemporary%20furniture%20design&width=400&height=300&seq=bedroom-1&orientation=portrait',
      subcategories: ['Beds & Frames', 'Nightstands', 'Dressers', 'Bedding'],
      description: 'Transform your bedroom into a peaceful retreat. Discover quality bedding, stylish furniture, and thoughtful accents that promote rest and relaxation.',
      link: '/bedroom'
    },
    {
      id: 'kitchen',
      name: 'Kitchen',
      icon: 'ri-restaurant-line',
      image: 'https://readdy.ai/api/search-image?query=Clean%20minimalist%20kitchen%20interior%20with%20modern%20appliances%2C%20neutral%20tones%2C%20organized%20space%2C%20simple%20elegant%20design%2C%20bright%20lighting%2C%20contemporary%20cabinetry%2C%20functional%20layout&width=400&height=300&seq=kitchen-1&orientation=portrait',
      subcategories: ['Dining Sets', 'Bar Stools', 'Storage', 'Cookware'],
      description: 'Elevate your culinary space with functional and beautiful kitchen essentials. From dining sets to smart storage solutions, make cooking and entertaining a joy.',
      link: '/kitchen'
    },
    {
      id: 'bathroom',
      name: 'Bathroom',
      icon: 'ri-drop-line',
      image: 'https://readdy.ai/api/search-image?query=Spa-like%20minimalist%20bathroom%20interior%20with%20clean%20fixtures%2C%20neutral%20tones%2C%20organized%20space%2C%20simple%20elegant%20design%2C%20soft%20lighting%2C%20contemporary%20fittings%2C%20serene%20atmosphere&width=400&height=300&seq=bathroom-1&orientation=portrait',
      subcategories: ['Vanities', 'Mirrors', 'Storage', 'Accessories'],
      description: 'Create a spa-like sanctuary in your own home. Find elegant vanities, stylish mirrors, and practical storage that combine beauty with functionality.',
      link: '/bathroom'
    },
    {
      id: 'home-office',
      name: 'Home Office',
      icon: 'ri-computer-line',
      image: 'https://readdy.ai/api/search-image?query=Productive%20minimalist%20home%20office%20interior%20with%20ergonomic%20desk%2C%20comfortable%20chair%2C%20neutral%20tones%2C%20organized%20workspace%2C%20simple%20elegant%20design%2C%20natural%20lighting%2C%20contemporary%20furniture&width=400&height=300&seq=home-office-1&orientation=portrait',
      subcategories: ['Desks', 'Office Chairs', 'Storage', 'Lighting'],
      description: 'Design a workspace that inspires productivity. Discover ergonomic furniture, smart storage, and stylish accessories for your perfect home office.',
      link: '/home-office'
    },
    {
      id: 'dining-room',
      name: 'Dining Room',
      icon: 'ri-goblet-line',
      image: 'https://readdy.ai/api/search-image?query=Elegant%20minimalist%20dining%20room%20interior%20with%20beautiful%20table%2C%20comfortable%20chairs%2C%20neutral%20tones%2C%20clean%20lines%2C%20inviting%20atmosphere%2C%20simple%20elegant%20decor%2C%20warm%20lighting%2C%20contemporary%20design&width=400&height=300&seq=dining-room-1&orientation=portrait',
      subcategories: ['Dining Tables', 'Dining Chairs', 'Buffets', 'Lighting'],
      description: 'Gather around a table that brings people together. Find dining sets, elegant lighting, and beautiful accents for memorable meals and conversations.',
      link: '/dining-room'
    },
    {
      id: 'outdoor',
      name: 'Outdoor',
      icon: 'ri-plant-line',
      image: 'https://readdy.ai/api/search-image?query=Inviting%20minimalist%20outdoor%20patio%20space%20with%20comfortable%20furniture%2C%20neutral%20tones%2C%20clean%20lines%2C%20relaxing%20atmosphere%2C%20simple%20elegant%20decor%2C%20natural%20elements%2C%20contemporary%20design&width=400&height=300&seq=outdoor-1&orientation=portrait',
      subcategories: ['Patio Furniture', 'Planters', 'Outdoor Lighting', 'Decor'],
      description: 'Extend your living space outdoors. Discover weather-resistant furniture, beautiful planters, and ambient lighting for your patio or garden.',
      link: '/outdoor'
    },
    {
      id: 'entryway',
      name: 'Entryway',
      icon: 'ri-door-line',
      image: 'https://readdy.ai/api/search-image?query=Welcoming%20minimalist%20entryway%20interior%20with%20organized%20storage%2C%20neutral%20tones%2C%20clean%20lines%2C%20inviting%20atmosphere%2C%20simple%20elegant%20decor%2C%20functional%20design%2C%20contemporary%20furniture&width=400&height=300&seq=entryway-1&orientation=portrait',
      subcategories: ['Console Tables', 'Coat Racks', 'Mirrors', 'Storage'],
      description: 'Make a great first impression with a welcoming entryway. Find stylish storage solutions, mirrors, and decor that set the tone for your home.',
      link: '/entryway'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Room Inspiration - Interior Decor Solutions"
        description="Explore curated decor ideas and products for every space in your home. Browse by room to find the perfect pieces for your living room, bedroom, kitchen, bathroom, and more."
        keywords="room inspiration, living room decor, bedroom ideas, kitchen design, bathroom decor, home office, dining room, outdoor furniture, entryway decor"
        canonical={`${siteUrl}/room-inspiration`}
        jsonLd={jsonLd}
      />

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white/95 backdrop-blur-sm shadow-sm">
        <div className="px-4 md:px-8 lg:px-20 h-[70px] flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <h1 className="text-[#1A4D4D] text-xl md:text-2xl lg:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '-0.02em' }}>
              Interior Decor Solutions
            </h1>
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            <Link to="/" className="text-[#1A4D4D] font-medium text-base hover:text-[#FF6B6B] transition-colors whitespace-nowrap">Home</Link>
            <Link to="/blog" className="text-[#1A4D4D] font-medium text-base hover:text-[#FF6B6B] transition-colors whitespace-nowrap">Blog</Link>
            <Link to="/room-inspiration" className="text-[#FF6B6B] font-medium text-base whitespace-nowrap">Room Inspiration</Link>
            <Link to="/reviews" className="text-[#1A4D4D] font-medium text-base hover:text-[#FF6B6B] transition-colors whitespace-nowrap">Reviews</Link>
            <Link to="/about" className="text-[#1A4D4D] font-medium text-base hover:text-[#FF6B6B] transition-colors whitespace-nowrap">About</Link>
          </div>

          <button className="hidden lg:flex bg-[#1A4D4D] text-white px-6 py-2.5 rounded-full font-medium text-sm hover:bg-[#0F3333] transition-all whitespace-nowrap items-center gap-2">
            <i className="ri-mail-line"></i>
            Get Ideas
          </button>

          {/* Mobile Menu Button */}
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center text-[#1A4D4D]"
          >
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-200">
            <div className="px-4 py-4 space-y-3">
              <Link to="/" className="block text-[#1A4D4D] font-medium text-base py-2">Home</Link>
              <Link to="/blog" className="block text-[#1A4D4D] font-medium text-base py-2">Blog</Link>
              <Link to="/room-inspiration" className="block text-[#FF6B6B] font-medium text-base py-2">Room Inspiration</Link>
              <Link to="/reviews" className="block text-[#1A4D4D] font-medium text-base py-2">Reviews</Link>
              <Link to="/about" className="block text-[#1A4D4D] font-medium text-base py-2">About</Link>
              <button className="w-full bg-[#1A4D4D] text-white px-6 py-2.5 rounded-full font-medium text-sm hover:bg-[#0F3333] transition-all whitespace-nowrap flex items-center justify-center gap-2">
                <i className="ri-mail-line"></i>
                Get Ideas
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden mt-[70px]">
        <div className="absolute inset-0">
          <img 
            src="https://readdy.ai/api/search-image?query=beautiful%20collection%20of%20modern%20home%20interior%20rooms%20showcasing%20different%20spaces%20including%20living%20room%20bedroom%20kitchen%20bathroom%20with%20elegant%20furniture%20neutral%20tones%20and%20cozy%20atmosphere%20creating%20inspiring%20home%20design%20gallery&width=1920&height=1080&seq=room-hero&orientation=landscape"
            alt="Room Inspiration"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>

        <div className="relative z-10 w-full px-4 md:px-8 lg:px-20 text-center">
          <h1 className="text-white text-4xl md:text-5xl lg:text-7xl font-light mb-6 leading-tight" style={{ fontFamily: 'Georgia, serif' }}>
            Inspiration for Every Room
          </h1>
          <p className="text-white/90 text-lg md:text-xl max-w-2xl mx-auto">
            Explore curated decor ideas and products for every space in your home
          </p>
        </div>
      </section>

      {/* Room Categories Section */}
      {roomCategories.map((room, index) => (
        <section 
          key={room.id} 
          id={room.id}
          className={`py-12 md:py-16 lg:py-20 px-4 md:px-8 lg:px-20 ${index % 2 === 0 ? 'bg-white' : 'bg-[#F9F7F4]'}`}
        >
          <div className="max-w-7xl mx-auto">
            <div className={`grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
              {/* Image */}
              <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                <div className="w-full h-[400px] md:h-[500px] rounded-3xl overflow-hidden shadow-lg">
                  <img 
                    src={room.image}
                    alt={room.name}
                    className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                  />
                </div>
              </div>

              {/* Content */}
              <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 bg-[#1A4D4D] rounded-xl flex items-center justify-center">
                    <i className={`${room.icon} text-white text-2xl`}></i>
                  </div>
                  <h2 className="text-[#1A4D4D] text-3xl md:text-4xl lg:text-5xl font-bold">
                    {room.name}
                  </h2>
                </div>

                <p className="text-gray-600 text-base md:text-lg leading-relaxed mb-8">
                  {room.description}
                </p>

                <div className="mb-8">
                  <h3 className="text-[#1A4D4D] text-lg font-semibold mb-4">Popular Categories:</h3>
                  <div className="grid grid-cols-2 gap-3">
                    {room.subcategories.map((sub, subIndex) => (
                      <div key={subIndex} className="flex items-center gap-2 text-gray-700">
                        <i className="ri-checkbox-circle-fill text-[#1A4D4D]"></i>
                        <span className="text-sm md:text-base">{sub}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <Link 
                  to={room.link}
                  className="inline-flex items-center gap-2 bg-[#1A4D4D] text-white px-8 py-3 rounded-full font-medium hover:bg-[#0F3333] transition-all whitespace-nowrap text-sm md:text-base"
                >
                  Explore {room.name} <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* CTA Section */}
      <section className="py-12 md:py-16 lg:py-20 px-4 md:px-8 lg:px-20 bg-[#1A4D4D]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-white text-3xl md:text-4xl lg:text-5xl font-light mb-6" style={{ fontFamily: 'Georgia, serif' }}>
            Ready to transform your space?
          </h2>
          <p className="text-white/80 text-base md:text-lg mb-8">
            Explore our curated collection of products and honest reviews
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/reviews"
              className="bg-white text-[#1A4D4D] px-8 py-3 rounded-full font-medium hover:bg-gray-100 transition-all whitespace-nowrap text-sm md:text-base"
            >
              Browse All Products
            </Link>
            <Link 
              to="/blog"
              className="bg-transparent border-2 border-white text-white px-8 py-3 rounded-full font-medium hover:bg-white/10 transition-all whitespace-nowrap text-sm md:text-base"
            >
              Read Our Blog
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative bg-[#1A4D4D] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-[0.08]">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <pattern id="home-pattern" x="0" y="0" width="80" height="80" patternUnits="userSpaceOnUse">
              <path d="M40 20 L50 30 L50 45 L30 45 L30 30 Z" stroke="white" fill="none" strokeWidth="1"/>
              <circle cx="15" cy="60" r="3" stroke="white" fill="none" strokeWidth="1"/>
              <rect x="60" y="55" width="8" height="8" stroke="white" fill="none" strokeWidth="1"/>
            </pattern>
            <rect width="100%" height="100%" fill="url(#home-pattern)"/>
          </svg>
        </div>

        <div className="relative z-10 px-4 md:px-8 lg:px-20 py-8 md:py-12 lg:py-16">
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-10 gap-8 md:gap-10">
            <div className="lg:col-span-3">
              <div className="flex items-center gap-2 mb-6">
                <img src="https://public.readdy.ai/ai/img_res/9e46e3d9-adac-48a0-871c-356cdcae054d.png" alt="Simple Home Decor" className="h-8 w-auto brightness-0 invert" />
              </div>
              
              <p className="text-white text-2xl md:text-3xl font-light leading-tight" style={{ fontFamily: 'Georgia, serif' }}>
                Simple touches.<br />
                Cozy homes.
              </p>
            </div>

            <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
              <div>
                <h4 className="text-white text-base md:text-lg font-semibold mb-3 md:mb-4">Shop</h4>
                <ul className="space-y-2 md:space-y-3">
                  <li><Link to="/room-inspiration#living-room" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Living Room</Link></li>
                  <li><Link to="/room-inspiration#bedroom" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Bedroom</Link></li>
                  <li><Link to="/room-inspiration#kitchen" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Kitchen</Link></li>
                </ul>
              </div>

              <div>
                <h4 className="text-white text-base md:text-lg font-semibold mb-3 md:mb-4">Learn</h4>
                <ul className="space-y-2 md:space-y-3">
                  <li><Link to="/blog" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Blog</Link></li>
                  <li><Link to="/reviews" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Reviews</Link></li>
                  <li><Link to="/blog" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Guides</Link></li>
                </ul>
              </div>

              <div>
                <h4 className="text-white text-base md:text-lg font-semibold mb-3 md:mb-4">Company</h4>
                <ul className="space-y-2 md:space-y-3">
                  <li><Link to="/about" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">About</Link></li>
                  <li><a href="#" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Contact</a></li>
                  <li><Link to="/affiliate-disclosure" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Affiliate Disclosure</Link></li>
                </ul>
              </div>

              <div>
                <h4 className="text-white text-base md:text-lg font-semibold mb-3 md:mb-4">Connect</h4>
                <ul className="space-y-2 md:space-y-3">
                  <li><a href="#" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Pinterest</a></li>
                  <li><a href="#" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Instagram</a></li>
                  <li><a href="#" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Newsletter</a></li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="relative z-10 border-t border-white/20 px-4 md:px-8 lg:px-20 py-6 md:py-8">
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
            <p className="text-white/80 text-xs md:text-sm text-center md:text-left">
              © 2026 Simple Home Decor. All rights reserved.
            </p>

            <div className="flex items-center gap-3 md:gap-4">
              <a href="#" className="w-9 h-9 md:w-10 md:h-10 border border-white/40 rounded-lg flex items-center justify-center hover:bg-white/10 transition-all whitespace-nowrap">
                <i className="ri-pinterest-line text-base md:text-lg"></i>
              </a>
              <a href="#" className="w-9 h-9 md:w-10 md:h-10 border border-white/40 rounded-lg flex items-center justify-center hover:bg-white/10 transition-all whitespace-nowrap">
                <i className="ri-instagram-line text-base md:text-lg"></i>
              </a>
              <a href="#" className="w-9 h-9 md:w-10 md:h-10 border border-white/40 rounded-lg flex items-center justify-center hover:bg-white/10 transition-all whitespace-nowrap">
                <i className="ri-facebook-line text-base md:text-lg"></i>
              </a>
              <a href="https://www.youtube.com/@InteriorDecorSolutions" target="_blank" rel="noopener noreferrer" className="w-9 h-9 md:w-10 md:h-10 border border-white/40 rounded-lg flex items-center justify-center hover:bg-white/10 transition-all whitespace-nowrap">
                <i className="ri-youtube-line text-base md:text-lg"></i>
              </a>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-3 md:gap-4 text-white/80 text-xs md:text-sm">
              <Link to="/privacy" className="hover:text-white transition-colors whitespace-nowrap">Privacy Policy</Link>
              <span className="hidden md:inline">|</span>
              <Link to="/terms" className="hover:text-white transition-colors whitespace-nowrap">Terms of Use</Link>
            </div>
          </div>
          
          <div className="max-w-7xl mx-auto mt-6 pt-6 border-t border-white/10">
            <p className="text-white/60 text-xs text-center leading-relaxed">
              Affiliate Disclosure: This website contains affiliate links. As an Amazon Associate, we earn from qualifying purchases at no additional cost to you.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
